import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
  ChartDataLabels
);

export const STATS_COLORS = {
  primary: "#4f46e5",
  primarySoft: "rgba(79, 70, 229, 0.12)",
  success: "#059669",
  successSoft: "rgba(5, 150, 105, 0.15)",
  warning: "#d97706",
  warningSoft: "rgba(217, 119, 6, 0.15)",
  danger: "#dc2626",
  slate: "#64748b",
  text: "#1e293b",
  textMuted: "#475569",
  grid: "rgba(148, 163, 184, 0.35)",
};

export const formatGnf = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n || 0);

/** Libellé graphique : montant complet + devise */
export const formatGnfLabel = (n) => {
  const v = Number(n) || 0;
  return v > 0 ? `${formatGnf(v)} GNF` : "";
};

const labelFont = {
  family: "'Segoe UI', system-ui, sans-serif",
  weight: "700",
};

/** Fond blanc derrière le texte pour lisibilité */
const labelBg = {
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  borderColor: "#cbd5e1",
  borderWidth: 1,
  borderRadius: 6,
  padding: { top: 3, bottom: 3, left: 5, right: 5 },
};

/** Libellés au-dessus des barres (montants GNF) */
export const datalabelsBarGnf = {
  display: true,
  anchor: "end",
  align: "top",
  offset: 4,
  color: STATS_COLORS.text,
  font: { ...labelFont, size: 10 },
  ...labelBg,
  formatter: (value) => formatGnfLabel(value),
  clip: false,
};

/** Libellés barres — valeurs entières (congés) */
export const datalabelsBarCount = {
  display: true,
  anchor: "end",
  align: "top",
  offset: 4,
  color: STATS_COLORS.text,
  font: { ...labelFont, size: 11 },
  ...labelBg,
  formatter: (value) => (value > 0 ? String(Math.round(value)) : ""),
  clip: false,
};

/** Libellés sur les points des courbes (GNF) */
export const datalabelsLineGnf = {
  display: (ctx) => {
    const len = ctx.dataset?.data?.length || 0;
    return len > 0 && len <= 14;
  },
  anchor: "end",
  align: "top",
  offset: 6,
  color: STATS_COLORS.text,
  font: { ...labelFont, size: 9 },
  ...labelBg,
  formatter: (value) => formatGnfLabel(value),
  clip: false,
};

/** Libellés courbes — compteurs */
export const datalabelsLineCount = {
  display: (ctx) => {
    const len = ctx.dataset?.data?.length || 0;
    return len > 0 && len <= 14;
  },
  anchor: "end",
  align: "top",
  offset: 6,
  color: STATS_COLORS.text,
  font: { ...labelFont, size: 10 },
  ...labelBg,
  formatter: (value) => (value > 0 ? String(value) : ""),
  clip: false,
};

/** Libellés donut / camembert — % + valeur */
export const datalabelsArc = {
  display: (ctx) => {
    const value = ctx.dataset.data[ctx.dataIndex];
    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
    if (!total || !value) return false;
    return (value / total) * 100 >= 6;
  },
  color: "#ffffff",
  font: { ...labelFont, size: 11 },
  textStrokeColor: "rgba(15, 23, 42, 0.35)",
  textStrokeWidth: 2,
  formatter: (value, ctx) => {
    const total = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
    const pct = total ? ((value / total) * 100).toFixed(0) : 0;
    return `${pct}%\n${formatGnf(value)}`;
  },
  textAlign: "center",
};

const pluginsBase = {
  legend: {
    labels: {
      color: STATS_COLORS.text,
      font: {
        size: 13,
        weight: "600",
        family: "'Segoe UI', system-ui, sans-serif",
      },
      padding: 18,
      boxWidth: 14,
      boxHeight: 14,
    },
  },
  tooltip: {
    backgroundColor: "#1e293b",
    titleColor: "#f8fafc",
    bodyColor: "#e2e8f0",
    padding: 12,
    cornerRadius: 8,
    titleFont: { size: 13, weight: "600" },
    bodyFont: { size: 12 },
  },
};

const tickX = {
  color: STATS_COLORS.text,
  font: { size: 12, weight: "500" },
  maxRotation: 45,
  minRotation: 0,
};

const tickY = {
  color: STATS_COLORS.text,
  font: { size: 12, weight: "500" },
};

export const cartesianScales = {
  x: {
    display: true,
    grid: { display: false },
    border: { color: "#cbd5e1", display: true },
    ticks: tickX,
    title: { display: false },
  },
  y: {
    display: true,
    grid: { color: STATS_COLORS.grid, drawBorder: false },
    border: { color: "#cbd5e1", display: true },
    ticks: {
      ...tickY,
      callback: (v) => formatGnf(v),
    },
  },
};

export const cartesianScalesCount = {
  x: {
    display: true,
    grid: { display: false },
    border: { color: "#cbd5e1" },
    ticks: tickX,
  },
  y: {
    display: true,
    grid: { color: STATS_COLORS.grid },
    border: { color: "#cbd5e1" },
    ticks: {
      ...tickY,
      callback: (v) => v,
    },
  },
};

export const arcScalesOff = {
  x: { display: false },
  y: { display: false },
};

const baseChart = {
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 28, right: 12, bottom: 8, left: 8 } },
  plugins: pluginsBase,
};

export const barChartOptions = {
  ...baseChart,
  scales: cartesianScales,
  plugins: {
    ...pluginsBase,
    datalabels: datalabelsBarGnf,
  },
};

export const lineChartOptions = {
  ...baseChart,
  interaction: { mode: "index", intersect: false },
  scales: cartesianScales,
  plugins: {
    ...pluginsBase,
    datalabels: datalabelsLineGnf,
  },
};

export const lineChartOptionsCount = {
  ...baseChart,
  scales: cartesianScalesCount,
  plugins: {
    ...pluginsBase,
    datalabels: datalabelsLineCount,
  },
};

export const doughnutChartOptions = {
  ...baseChart,
  cutout: "68%",
  scales: arcScalesOff,
  plugins: {
    ...pluginsBase,
    datalabels: datalabelsArc,
  },
};

export const pieChartOptions = {
  ...baseChart,
  scales: arcScalesOff,
  plugins: {
    ...pluginsBase,
    datalabels: datalabelsArc,
  },
};

export const barChartOptionsCount = {
  ...baseChart,
  scales: cartesianScalesCount,
  plugins: {
    ...pluginsBase,
    datalabels: datalabelsBarCount,
  },
};

/** @deprecated */
export const chartDefaults = barChartOptions;
